// src/components/dashboard/CoreAdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { reportsService } from '../../services/reportsService';
import { attendanceService } from '../../services/attendanceService';
import settingsService from '../../services/settingsService';
import LoadingSpinner from '../common/LoadingSpinner';
import { usePageTitle } from '../../hooks/usePageTitle';

const CoreAdminDashboard = () => {
  usePageTitle('Admin Dashboard');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [salesAgentReport, setSalesAgentReport] = useState({ summary: null, report_data: [] });
  const [systemActivity, setSystemActivity] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [revenueView, setRevenueView] = useState('monthly'); // 'daily' | 'monthly' | 'yearly'
  const [revenueCurrency, setRevenueCurrency] = useState('total'); // 'total' | 'pkr' | 'usd'
  const [staffView, setStaffView] = useState('summary'); // 'summary' | 'org' | 'premises'
  const [maintenanceRunning, setMaintenanceRunning] = useState('');
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [maintenanceError, setMaintenanceError] = useState('');
  const [backupOptions, setBackupOptions] = useState({
    label: 'manual',
    include_uploads: true,
    include_node_modules: false,
    include_vendor: true
  });
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupRunProgress, setBackupRunProgress] = useState(0);
  const [backupResult, setBackupResult] = useState(null);
  const [backupError, setBackupError] = useState('');
  const [backupList, setBackupList] = useState([]);
  const [backupDownloadProgress, setBackupDownloadProgress] = useState({});
  const [dbBackupRunning, setDbBackupRunning] = useState(false);
  const [dbBackupRunProgress, setDbBackupRunProgress] = useState(0);
  const [dbBackupResult, setDbBackupResult] = useState(null);
  const [dbBackupError, setDbBackupError] = useState('');
  const [dbBackupList, setDbBackupList] = useState([]);
  const [dbBackupDownloadProgress, setDbBackupDownloadProgress] = useState({});
  const [dbBackupConfig, setDbBackupConfig] = useState({
    auto_enabled: 0,
    frequency_hours: 24,
    keep_last: 7,
    last_run_at: ''
  });
  const [dbBackupSaving, setDbBackupSaving] = useState(false);
  const [cloudBackupConfig, setCloudBackupConfig] = useState({
    cloud_enabled: 0,
    google_drive_link: '',
    cloud_webhook_url: ''
  });
  const [cloudBackupSaving, setCloudBackupSaving] = useState(false);
  const [cloudBackupTesting, setCloudBackupTesting] = useState(false);
  const [cloudBackupTestResult, setCloudBackupTestResult] = useState(null);

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = useCallback(async () => {
    try {
      setLoading(true);
      const todayStr = new Date().toISOString().split('T')[0];

      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
      const [dashboardStats, todayAttendanceStats, salesAgentReportData] = await Promise.all([
        reportsService.getDashboardStats().catch(() => ({})),
        attendanceService.getAttendanceStats({ date_from: todayStr, date_to: todayStr }).catch(() => ({})),
        reportsService.getSalesAgentsReport({ date_from: monthStart, date_to: todayStr }).catch(() => ({ report_data: [] }))
      ]);

      setDashboardData(dashboardStats);
      setSalesAgentReport({
        summary: salesAgentReportData?.summary ?? null,
        report_data: Array.isArray(salesAgentReportData?.report_data) ? salesAgentReportData.report_data : []
      });

      // Build recent activity
      const activities = [];
      const totalOrgs = dashboardStats?.organization_stats?.total ?? 0;
      const totalUsers = dashboardStats?.staff_stats?.total ?? 0;
      const activePremises = dashboardStats?.premises_stats?.active ?? 0;
      const avgAttendance = dashboardStats?.avg_attendance ?? 0;

      if (totalOrgs > 0) {
        activities.push({
          id: 1, type: 'org', icon: 'fas fa-building', color: 'success',
          message: `System managing ${totalOrgs} organization${totalOrgs !== 1 ? 's' : ''} with ${totalUsers} staff members`,
          time: 'Current Status'
        });
      }
      if ((todayAttendanceStats?.total_present ?? 0) > 0) {
        activities.push({
          id: 2, type: 'attendance', icon: 'fas fa-user-check', color: 'info',
          message: `${todayAttendanceStats.total_present} staff members marked attendance today`,
          time: 'Today'
        });
      }
      if ((todayAttendanceStats?.total_late ?? 0) > 0) {
        activities.push({
          id: 3, type: 'late', icon: 'fas fa-exclamation-triangle', color: 'warning',
          message: `${todayAttendanceStats.total_late} late arrival${todayAttendanceStats.total_late !== 1 ? 's' : ''} across all organizations`,
          time: 'Today'
        });
      }
      if (activePremises > 0) {
        activities.push({
          id: 4, type: 'premises', icon: 'fas fa-map-marker-alt', color: 'info',
          message: `Monitoring ${activePremises} active premise${activePremises !== 1 ? 's' : ''} across all organizations`,
          time: 'Active'
        });
      }
      if (avgAttendance > 0) {
        activities.push({
          id: 5, type: 'perf', icon: 'fas fa-chart-line', color: avgAttendance >= 85 ? 'success' : 'warning',
          message: `System-wide attendance rate: ${Number(avgAttendance).toFixed(1)}%`,
          time: 'This Month'
        });
      }
      setSystemActivity(activities);

      // Alerts — each has a path + state so clicking navigates to pre-filtered view
      const alerts = [];
      const overdueOrgs = dashboardStats?.organization_stats?.payment_overdue ?? 0;
      if (overdueOrgs > 0) {
        alerts.push({
          id: 'payment_overdue_orgs', type: 'critical', icon: 'fas fa-exclamation-circle',
          title: 'Payment Overdue Organizations',
          message: `${overdueOrgs} organization${overdueOrgs !== 1 ? 's' : ''} ${overdueOrgs === 1 ? 'has' : 'have'} overdue payments and no active subscription.`,
          action: 'View overdue organizations →',
          path: '/organizations', state: { filter: 'payment_overdue' }
        });
      }
      const overdueSubs = dashboardStats?.subscriptions_stats?.overdue ?? 0;
      if (overdueSubs > 0) {
        alerts.push({
          id: 'overdue_subs', type: 'critical', icon: 'fas fa-id-card',
          title: 'Overdue Subscriptions',
          message: `${overdueSubs} subscription${overdueSubs !== 1 ? 's' : ''} ${overdueSubs === 1 ? 'has' : 'have'} passed their end date and need renewal.`,
          action: 'View in Payment Analytics →',
          path: '/analytics', state: {}
        });
      }
      if (avgAttendance > 0 && avgAttendance < 70) {
        alerts.push({
          id: 'low_attendance', type: 'warning', icon: 'fas fa-user-clock',
          title: 'Low System Attendance',
          message: `System-wide attendance is ${Number(avgAttendance).toFixed(1)}% this month — below the 70% threshold.`,
          action: 'View Attendance →',
          path: '/attendance', state: {}
        });
      }
      const inactiveOrgs = dashboardStats?.organization_stats?.inactive ?? 0;
      const totalOrgsForAlert = dashboardStats?.organization_stats?.total ?? 0;
      if (totalOrgsForAlert > 0 && inactiveOrgs > 0 && (inactiveOrgs / totalOrgsForAlert) > 0.2) {
        alerts.push({
          id: 'inactive_orgs', type: 'warning', icon: 'fas fa-building',
          title: 'High Inactive Organizations',
          message: `${inactiveOrgs} of ${totalOrgsForAlert} organizations (${Math.round((inactiveOrgs / totalOrgsForAlert) * 100)}%) are currently inactive.`,
          action: 'View inactive organizations →',
          path: '/organizations', state: { filter: 'inactive_orgs' }
        });
      }
      const inactiveStaff = dashboardStats?.staff_stats?.inactive ?? 0;
      const totalStaffForAlert = dashboardStats?.staff_stats?.total ?? 0;
      if (totalStaffForAlert > 0 && inactiveStaff > 0 && (inactiveStaff / totalStaffForAlert) > 0.3) {
        alerts.push({
          id: 'inactive_staff', type: 'info', icon: 'fas fa-users',
          title: 'High Inactive Staff',
          message: `${inactiveStaff} of ${totalStaffForAlert} staff members (${Math.round((inactiveStaff / totalStaffForAlert) * 100)}%) are currently inactive.`,
          action: 'View inactive staff →',
          path: '/staff', state: { filter: 'inactive' }
        });
      }
      setSystemAlerts(alerts);
      const backupsResponse = await settingsService.getBackups().catch(() => ({ backups: [] }));
      setBackupList(Array.isArray(backupsResponse?.backups) ? backupsResponse.backups : []);
      const dbBackupsResponse = await settingsService.getDatabaseBackups().catch(() => ({ backups: [] }));
      setDbBackupList(Array.isArray(dbBackupsResponse?.backups) ? dbBackupsResponse.backups : []);
      const dbConfig = await settingsService.getDatabaseBackupConfig().catch(() => null);
      if (dbConfig) {
        setDbBackupConfig((prev) => ({ ...prev, ...dbConfig }));
      }
      const cloudConfig = await settingsService.getBackupCloudConfig().catch(() => null);
      if (cloudConfig) {
        setCloudBackupConfig((prev) => ({ ...prev, ...cloudConfig }));
      }

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const coreAdminActions = [
    { title: 'Landing Page Manager', icon: 'fas fa-home', link: '/admin/landing-page', color: 'teal', description: 'Manage landing page content, carousel, and sections' },
    { title: 'System Analytics', icon: 'fas fa-chart-line', link: '/reports', color: 'primary', description: 'View system-wide performance and usage analytics' },
    { title: 'Payment Gateways', icon: 'fas fa-credit-card', link: '/payment-gateways', color: 'success', description: 'Manage payment gateways and billing systems' },
    { title: 'User Management', icon: 'fas fa-users-cog', link: '/staff', color: 'info', description: 'Manage all users across organizations' },
    { title: 'System Settings', icon: 'fas fa-cogs', link: '/admin/settings', color: 'warning', description: 'Configure global system settings' },
    { title: 'Organizations', icon: 'fas fa-building', link: '/organizations', color: 'purple', description: 'Manage all registered organizations' },
    { title: 'Audit Trail', icon: 'fas fa-history', link: '/admin/audit-trail', color: 'secondary', description: 'View system audit logs and activity history' }
  ];

  const runMaintenanceAction = async (mode) => {
    try {
      setMaintenanceRunning(mode);
      setMaintenanceError('');
      setMaintenanceResult(null);
      const response = await settingsService.runMaintenance(mode, 200);
      const data = response?.data || response || {};
      setMaintenanceResult(data);
      if (mode === 'full') {
        await loadSystemData();
      }
    } catch (err) {
      setMaintenanceError(err?.response?.data?.message || err.message || 'Maintenance run failed');
    } finally {
      setMaintenanceRunning('');
    }
  };

  const runIntegrationSyncAction = async () => {
    try {
      setMaintenanceRunning('integration_sync');
      setMaintenanceError('');
      setMaintenanceResult(null);
      const response = await settingsService.runIntegrationSync();
      const data = response?.data || response || {};
      setMaintenanceResult({ attendance_integration_sync: data });
    } catch (err) {
      setMaintenanceError(err?.response?.data?.message || err.message || 'Integration sync failed');
    } finally {
      setMaintenanceRunning('');
    }
  };

  const runBackupAction = async () => {
    let progressTimer = null;
    try {
      setBackupRunning(true);
      setBackupRunProgress(6);
      setBackupError('');
      setBackupResult(null);
      progressTimer = setInterval(() => {
        setBackupRunProgress((prev) => (prev >= 92 ? prev : prev + 6));
      }, 800);
      const response = await settingsService.runBackup(backupOptions);
      clearInterval(progressTimer);
      setBackupRunProgress(100);
      setBackupResult(response?.data || response || null);
      const backupsResponse = await settingsService.getBackups().catch(() => ({ backups: [] }));
      setBackupList(Array.isArray(backupsResponse?.backups) ? backupsResponse.backups : []);
    } catch (err) {
      if (progressTimer) clearInterval(progressTimer);
      setBackupError(err?.response?.data?.message || err.message || 'Backup failed');
    } finally {
      setBackupRunning(false);
      setTimeout(() => setBackupRunProgress(0), 1200);
    }
  };

  const runDatabaseBackupAction = async () => {
    let progressTimer = null;
    try {
      setDbBackupRunning(true);
      setDbBackupRunProgress(8);
      setDbBackupError('');
      setDbBackupResult(null);
      progressTimer = setInterval(() => {
        setDbBackupRunProgress((prev) => (prev >= 92 ? prev : prev + 7));
      }, 700);
      const response = await settingsService.runDatabaseBackup();
      clearInterval(progressTimer);
      setDbBackupRunProgress(100);
      setDbBackupResult(response?.data || response || null);
      const dbBackupsResponse = await settingsService.getDatabaseBackups().catch(() => ({ backups: [] }));
      setDbBackupList(Array.isArray(dbBackupsResponse?.backups) ? dbBackupsResponse.backups : []);
      const dbConfig = await settingsService.getDatabaseBackupConfig().catch(() => null);
      if (dbConfig) setDbBackupConfig((prev) => ({ ...prev, ...dbConfig }));
    } catch (err) {
      if (progressTimer) clearInterval(progressTimer);
      setDbBackupError(err?.response?.data?.message || err.message || 'Database backup failed');
    } finally {
      setDbBackupRunning(false);
      setTimeout(() => setDbBackupRunProgress(0), 1200);
    }
  };

  const handleDownloadBackup = async (file) => {
    const fileName = file.file_name;
    try {
      setBackupDownloadProgress((prev) => ({ ...prev, [fileName]: 0 }));
      await settingsService.downloadBackupFile(fileName, (pct) => {
        setBackupDownloadProgress((prev) => ({ ...prev, [fileName]: pct }));
      });
      setBackupDownloadProgress((prev) => ({ ...prev, [fileName]: 100 }));
      setTimeout(() => {
        setBackupDownloadProgress((prev) => {
          const next = { ...prev };
          delete next[fileName];
          return next;
        });
      }, 1000);
    } catch (e) {
      setBackupError(e?.message || 'Failed to download backup');
      setBackupDownloadProgress((prev) => {
        const next = { ...prev };
        delete next[fileName];
        return next;
      });
    }
  };

  const handleDownloadDbBackup = async (file) => {
    const fileName = file.file_name;
    try {
      setDbBackupDownloadProgress((prev) => ({ ...prev, [fileName]: 0 }));
      await settingsService.downloadDatabaseBackupFile(fileName, (pct) => {
        setDbBackupDownloadProgress((prev) => ({ ...prev, [fileName]: pct }));
      });
      setDbBackupDownloadProgress((prev) => ({ ...prev, [fileName]: 100 }));
      setTimeout(() => {
        setDbBackupDownloadProgress((prev) => {
          const next = { ...prev };
          delete next[fileName];
          return next;
        });
      }, 1000);
    } catch (e) {
      setDbBackupError(e?.message || 'Failed to download database backup');
      setDbBackupDownloadProgress((prev) => {
        const next = { ...prev };
        delete next[fileName];
        return next;
      });
    }
  };

  const saveDatabaseBackupConfig = async () => {
    try {
      setDbBackupSaving(true);
      setDbBackupError('');
      const payload = {
        auto_enabled: dbBackupConfig.auto_enabled ? 1 : 0,
        frequency_hours: Number(dbBackupConfig.frequency_hours || 24),
        keep_last: Number(dbBackupConfig.keep_last || 7)
      };
      const response = await settingsService.updateDatabaseBackupConfig(payload);
      setDbBackupConfig((prev) => ({ ...prev, ...(response?.data || response || payload) }));
    } catch (err) {
      setDbBackupError(err?.response?.data?.message || err.message || 'Failed to save database backup settings');
    } finally {
      setDbBackupSaving(false);
    }
  };

  const saveCloudBackupConfig = async () => {
    try {
      setCloudBackupSaving(true);
      setBackupError('');
      setDbBackupError('');
      const payload = {
        cloud_enabled: cloudBackupConfig.cloud_enabled ? 1 : 0,
        google_drive_link: cloudBackupConfig.google_drive_link || '',
        cloud_webhook_url: cloudBackupConfig.cloud_webhook_url || ''
      };
      const response = await settingsService.updateBackupCloudConfig(payload);
      setCloudBackupConfig((prev) => ({ ...prev, ...(response?.data || response || payload) }));
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to save cloud backup settings';
      setBackupError(msg);
      setDbBackupError(msg);
    } finally {
      setCloudBackupSaving(false);
    }
  };

  const testCloudBackupWebhook = async () => {
    try {
      setCloudBackupTesting(true);
      setCloudBackupTestResult(null);
      setBackupError('');
      const result = await settingsService.testBackupCloudWebhook();
      setCloudBackupTestResult(result?.data || result || null);
    } catch (err) {
      setBackupError(err?.response?.data?.message || err.message || 'Cloud webhook test failed');
    } finally {
      setCloudBackupTesting(false);
    }
  };

  const fmt = (n) => Number(n ?? 0).toLocaleString();
  const fmtCurrency = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const getRevenueGraphData = () => {
    if (!dashboardData?.revenue_stats) return [];
    const key = revenueCurrency === 'pkr' ? 'pkr' : revenueCurrency === 'usd' ? 'usd' : 'total';
    let source, labelFn;
    if (revenueView === 'daily') {
      source = dashboardData.revenue_stats.daily_graph ?? [];
      labelFn = item => {
        const d = new Date(item.day_key);
        return `${d.getDate()}/${d.getMonth() + 1}`;
      };
    } else if (revenueView === 'yearly') {
      source = dashboardData.revenue_stats.yearly_graph ?? [];
      labelFn = item => String(item.year_key);
    } else {
      source = dashboardData.revenue_stats.monthly_graph ?? [];
      labelFn = item => {
        const [yr, mo] = item.month_key.split('-');
        return new Date(yr, mo - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
      };
    }
    return source.map(item => ({ label: labelFn(item), value: Number(item[key] ?? 0) }));
  };

  // Compute period totals for summary KPIs
  const getRevenuePeriodSummary = () => {
    const rev = dashboardData?.revenue_stats ?? {};
    const graphData = getRevenueGraphData();
    const periodTotal = graphData.reduce((s, d) => s + d.value, 0);
    const prevPeriodTotal = (() => {
      if (revenueView === 'monthly') {
        // Second-to-last month in graph
        const src = rev.monthly_graph ?? [];
        const key = revenueCurrency === 'pkr' ? 'pkr' : revenueCurrency === 'usd' ? 'usd' : 'total';
        return src.length >= 2 ? Number(src[src.length - 2]?.[key] ?? 0) : 0;
      }
      if (revenueView === 'yearly') {
        const src = rev.yearly_graph ?? [];
        const key = revenueCurrency === 'pkr' ? 'pkr' : revenueCurrency === 'usd' ? 'usd' : 'total';
        return src.length >= 2 ? Number(src[src.length - 2]?.[key] ?? 0) : 0;
      }
      return 0;
    })();
    const currentPeriod = revenueView === 'monthly'
      ? graphData[graphData.length - 1]?.value ?? 0
      : revenueView === 'daily' ? periodTotal : graphData[graphData.length - 1]?.value ?? 0;
    const growth = prevPeriodTotal > 0
      ? (((currentPeriod - prevPeriodTotal) / prevPeriodTotal) * 100).toFixed(1)
      : null;
    return { periodTotal, currentPeriod, prevPeriodTotal, growth };
  };

  const revenueGraphData = getRevenueGraphData();
  const maxRevenueVal = Math.max(...revenueGraphData.map(d => d.value), 1);

  if (loading) return <LoadingSpinner message="Loading system dashboard..." />;

  const os = dashboardData?.organization_stats ?? {};
  const ps = dashboardData?.premises_stats ?? {};
  const ss = dashboardData?.staff_stats ?? {};
  const as = dashboardData?.attendance_stats ?? {};
  const subs = dashboardData?.subscriptions_stats ?? {};
  const rev = dashboardData?.revenue_stats ?? {};
  const salesSummary = salesAgentReport?.summary ?? null;
  const salesRows = Array.isArray(salesAgentReport?.report_data) ? salesAgentReport.report_data : [];
  const topSalesAgent = salesRows.length > 0
    ? [...salesRows].sort((a, b) => Number(b.total_collected ?? 0) - Number(a.total_collected ?? 0))[0]
    : null;
  const attPct = Number(as.attendance_percent ?? 0);

  // Per-currency totals derived from individual agent rows (summary doesn't split by currency)
  const salesPkrCollected = salesRows.reduce((s, r) => (String(r.currency ?? '').toUpperCase() === 'PKR' ? s + Number(r.total_collected ?? 0) : s), 0);
  const salesUsdCollected = salesRows.reduce((s, r) => (['USD', 'US$'].includes(String(r.currency ?? '').toUpperCase()) ? s + Number(r.total_collected ?? 0) : s), 0);
  const salesPkrCommission = salesRows.reduce((s, r) => (String(r.currency ?? '').toUpperCase() === 'PKR' ? s + Number(r.commission_earned ?? 0) : s), 0);
  const salesUsdCommission = salesRows.reduce((s, r) => (['USD', 'US$'].includes(String(r.currency ?? '').toUpperCase()) ? s + Number(r.commission_earned ?? 0) : s), 0);

  // Average hours per day this month (monthly_hours / days elapsed)
  const daysElapsed = Math.max(1, new Date().getDate());
  const avgHoursPerDay = dashboardData?.monthly_hours > 0
    ? (Number(dashboardData.monthly_hours) / daysElapsed).toFixed(1)
    : '0.0';

  return (
    <div className="core-admin-dashboard">
      <div className="container">

        {/* Welcome Header */}
        <div className="welcome-section">
          <div className="welcome-content">
            <div className="admin-badge">
              <i className="fas fa-crown"></i>
              Core Administrator
            </div>
            <h1>System Control Center</h1>
            <p>Monitor and manage the entire Haazri ecosystem</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {/* Alerts */}
        {systemAlerts.filter(a => !dismissedAlerts.has(a.id)).length > 0 && (
          <div className="alerts-section">
            <div className="alerts-section-header">
              <h3><i className="fas fa-bell"></i> System Alerts</h3>
              <span className="alerts-count">{systemAlerts.filter(a => !dismissedAlerts.has(a.id)).length} active</span>
            </div>
            <div className="alerts-grid">
              {systemAlerts.filter(a => !dismissedAlerts.has(a.id)).map(alert => (
                <div
                  key={alert.id}
                  className={`alert-card ${alert.type} clickable`}
                  onClick={() => navigate(alert.path, { state: alert.state })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && navigate(alert.path, { state: alert.state })}
                >
                  <div className="alert-header">
                    <div className="alert-title-row">
                      <i className={`${alert.icon} alert-icon-sm`}></i>
                      <span className="alert-title">{alert.title}</span>
                    </div>
                    <button
                      className="alert-dismiss"
                      onClick={e => { e.stopPropagation(); setDismissedAlerts(prev => new Set([...prev, alert.id])); }}
                      title="Dismiss"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-action">{alert.action}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── STATS GROUPS GRID ─── */}
        <div className="stats-groups-grid">

          {/* 1. Organizations */}
          <div className="stats-group-card clickable" onClick={() => navigate('/organizations')} style={{ '--group-color': '#3498db' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #3498db, #2980b9)' }}>
                <i className="fas fa-building"></i>
              </div>
              <div className="group-title-area">
                <h3>Organizations</h3>
                <div className="group-total">{fmt(os.total)} Total</div>
              </div>
              <i className="fas fa-arrow-right group-arrow"></i>
            </div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-check-circle"></i> {fmt(os.active)} Active</span>
              <span className="badge free"><i className="fas fa-gift"></i> {fmt(os.free)} Free</span>
              <span className="badge overdue"><i className="fas fa-exclamation-circle"></i> {fmt(os.payment_overdue)} Payment Overdue</span>
              <span className="badge inactive"><i className="fas fa-pause-circle"></i> {fmt(os.inactive)} Inactive</span>
            </div>
          </div>

          {/* 2. Premises */}
          <div className="stats-group-card clickable" onClick={() => navigate('/premises')} style={{ '--group-color': '#f39c12' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #f39c12, #e67e22)' }}>
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="group-title-area">
                <h3>Premises</h3>
                <div className="group-total">{fmt((ps.active ?? 0) + (ps.inactive ?? 0))} Total</div>
              </div>
              <i className="fas fa-arrow-right group-arrow"></i>
            </div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-check-circle"></i> {fmt(ps.active)} Active</span>
              <span className="badge free"><i className="fas fa-gift"></i> {fmt(ps.free)} Free</span>
              <span className="badge overdue"><i className="fas fa-exclamation-circle"></i> {fmt(ps.payment_overdue)} Payment Overdue</span>
              <span className="badge inactive"><i className="fas fa-pause-circle"></i> {fmt(ps.inactive)} Inactive</span>
            </div>
            <div className="group-divider"></div>
            <div className="group-sub-label">Users on Premises</div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-user-check"></i> {fmt(ps.users_active)} Active</span>
              <span className="badge inactive"><i className="fas fa-user-slash"></i> {fmt(ps.users_inactive)} Inactive</span>
            </div>
          </div>

          {/* 3. Staff */}
          <div className="stats-group-card" style={{ '--group-color': '#27ae60' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #27ae60, #229954)' }}>
                <i className="fas fa-users"></i>
              </div>
              <div className="group-title-area">
                <h3>Staff</h3>
                <div className="group-total">{fmt(ss.total)} Total</div>
              </div>
              <Link to="/staff" className="view-link" onClick={e => e.stopPropagation()}>View <i className="fas fa-arrow-right"></i></Link>
            </div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-user-check"></i> {fmt(ss.active)} Active</span>
              <span className="badge inactive"><i className="fas fa-user-slash"></i> {fmt(ss.inactive)} Inactive</span>
            </div>
            <div className="group-tabs">
              <button className={`tab-btn ${staffView === 'summary' ? 'active' : ''}`} onClick={() => setStaffView('summary')}>Summary</button>
              <button className={`tab-btn ${staffView === 'org' ? 'active' : ''}`} onClick={() => setStaffView('org')}>Org-wise</button>
              <button className={`tab-btn ${staffView === 'premises' ? 'active' : ''}`} onClick={() => setStaffView('premises')}>Premises-wise</button>
            </div>
            {staffView === 'summary' && (
              <div className="staff-summary-bars">
                <div className="mini-bar-row">
                  <span className="mini-bar-label">Active</span>
                  <div className="mini-bar-track">
                    <div className="mini-bar-fill success" style={{ width: ss.total > 0 ? `${(ss.active / ss.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="mini-bar-pct">{ss.total > 0 ? Math.round((ss.active / ss.total) * 100) : 0}%</span>
                </div>
                <div className="mini-bar-row">
                  <span className="mini-bar-label">Inactive</span>
                  <div className="mini-bar-track">
                    <div className="mini-bar-fill danger" style={{ width: ss.total > 0 ? `${(ss.inactive / ss.total) * 100}%` : '0%' }}></div>
                  </div>
                  <span className="mini-bar-pct">{ss.total > 0 ? Math.round((ss.inactive / ss.total) * 100) : 0}%</span>
                </div>
              </div>
            )}
            {staffView === 'org' && (
              <div className="breakdown-list">
                {(ss.organization_wise ?? []).length === 0 ? (
                  <div className="breakdown-empty">No data</div>
                ) : (ss.organization_wise ?? []).map((org, i) => (
                  <div key={i} className="breakdown-row">
                    <span className="breakdown-name">{org.organization_name}</span>
                    <span className="breakdown-vals">
                      <span className="bv total">{fmt(org.total)}</span>
                      <span className="bv active">{fmt(org.active)}</span>
                      <span className="bv inactive">{fmt(org.inactive)}</span>
                    </span>
                  </div>
                ))}
                <div className="breakdown-legend">
                  <span className="bv total">Total</span>
                  <span className="bv active">Active</span>
                  <span className="bv inactive">Inactive</span>
                </div>
              </div>
            )}
            {staffView === 'premises' && (
              <div className="breakdown-list">
                {(ss.premises_wise ?? []).length === 0 ? (
                  <div className="breakdown-empty">No data</div>
                ) : (ss.premises_wise ?? []).map((p, i) => (
                  <div key={i} className="breakdown-row">
                    <span className="breakdown-name">{p.premises_name}<span className="breakdown-org-name">{p.organization_name}</span></span>
                    <span className="breakdown-vals">
                      <span className="bv total">{fmt(p.total)}</span>
                      <span className="bv active">{fmt(p.active)}</span>
                      <span className="bv inactive">{fmt(p.inactive)}</span>
                    </span>
                  </div>
                ))}
                <div className="breakdown-legend">
                  <span className="bv total">Total</span>
                  <span className="bv active">Active</span>
                  <span className="bv inactive">Inactive</span>
                </div>
              </div>
            )}
          </div>

          {/* 4. Attendance */}
          <div className="stats-group-card clickable" onClick={() => navigate('/attendance')} style={{ '--group-color': '#9b59b6' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #9b59b6, #8e44ad)' }}>
                <i className="fas fa-user-clock"></i>
              </div>
              <div className="group-title-area">
                <h3>Attendance</h3>
                <div className="group-total">{attPct.toFixed(1)}% Rate</div>
              </div>
              <i className="fas fa-arrow-right group-arrow"></i>
            </div>
            <div className="attendance-ring-row">
              <div className="attendance-ring" style={{ '--pct': attPct }}>
                <svg viewBox="0 0 36 36" className="ring-svg">
                  <circle className="ring-bg" cx="18" cy="18" r="15.9" fill="none" strokeWidth="3" />
                  <circle className="ring-fill" cx="18" cy="18" r="15.9" fill="none" strokeWidth="3"
                    strokeDasharray={`${attPct} ${100 - attPct}`} strokeDashoffset="25" />
                </svg>
                <div className="ring-label">{attPct.toFixed(0)}%</div>
              </div>
              <div className="group-badges vertical">
                <span className="badge success"><i className="fas fa-check"></i> {fmt(as.present)} Present</span>
                <span className="badge danger"><i className="fas fa-times"></i> {fmt(as.absent)} Absent</span>
                <span className="badge warning"><i className="fas fa-clock"></i> {fmt(as.late)} Late</span>
                <span className="badge info"><i className="fas fa-calendar-minus"></i> {fmt(as.leave)} Leave</span>
                <span className="badge neutral"><i className="fas fa-umbrella-beach"></i> {fmt(as.holiday_weekend)} Holiday/Weekend</span>
              </div>
            </div>
            <div className="att-analytics-strip">
              <div className="att-kpi">
                <div className="att-kpi-label">Avg Attendance</div>
                <div className="att-kpi-value">{attPct.toFixed(1)}%</div>
              </div>
              <div className="att-kpi">
                <div className="att-kpi-label">Total Staff</div>
                <div className="att-kpi-value">{fmt(ss.total ?? 0)}</div>
              </div>
              <div className="att-kpi">
                <div className="att-kpi-label">Avg Hours / Day</div>
                <div className="att-kpi-value">{avgHoursPerDay}h</div>
              </div>
            </div>
            <div className="group-footer-note">This month · {fmt(as.total)} total records</div>
          </div>

          {/* 5. Subscriptions */}
          <div className="stats-group-card clickable" onClick={() => navigate('/admin/payment-management')} style={{ '--group-color': '#17a2b8' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #17a2b8, #138496)' }}>
                <i className="fas fa-id-card"></i>
              </div>
              <div className="group-title-area">
                <h3>Subscriptions</h3>
                <div className="group-total">{fmt(subs.total)} Total</div>
              </div>
              <i className="fas fa-arrow-right group-arrow"></i>
            </div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-check-circle"></i> {fmt(subs.active)} Active</span>
              <span className="badge inactive"><i className="fas fa-ban"></i> {fmt(subs.inactive)} Inactive</span>
              <span className="badge free"><i className="fas fa-gift"></i> {fmt(subs.free)} Free</span>
              <span className="badge overdue"><i className="fas fa-exclamation-circle"></i> {fmt(subs.overdue)} Overdue</span>
            </div>
          </div>

          {/* 6. Sales Agents */}
          <div className="stats-group-card clickable" onClick={() => navigate('/reports')} style={{ '--group-color': '#0f766e' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #0f766e, #115e59)' }}>
                <i className="fas fa-user-tie"></i>
              </div>
              <div className="group-title-area">
                <h3>Sales Agents</h3>
                <div className="group-total">{fmt(salesSummary?.agents_count ?? salesRows.length)} Agent{(salesSummary?.agents_count ?? salesRows.length) === 1 ? '' : 's'}</div>
              </div>
              <i className="fas fa-arrow-right group-arrow"></i>
            </div>
            <div className="group-badges">
              <span className="badge success"><i className="fas fa-building"></i> {fmt(salesSummary?.organizations_assigned ?? 0)} Assigned Orgs</span>
              <span className="badge info"><i className="fas fa-check-circle"></i> {fmt(salesSummary?.active_organizations ?? 0)} Active Orgs</span>
              <span className="badge warning"><i className="fas fa-receipt"></i> {fmt(salesSummary?.completed_payments ?? 0)} Paid Invoices</span>
            </div>
            <div className="group-divider"></div>
            <div className="group-sub-label">Collections (selected period)</div>
            <div className="group-badges vertical">
              {salesPkrCollected > 0 && <span className="badge success"><i className="fas fa-coins"></i> ₨ {fmtCurrency(salesPkrCollected)} Collected (PKR)</span>}
              {salesUsdCollected > 0 && <span className="badge success"><i className="fas fa-dollar-sign"></i> $ {fmtCurrency(salesUsdCollected)} Collected (USD)</span>}
              {salesPkrCommission > 0 && <span className="badge free"><i className="fas fa-percent"></i> ₨ {fmtCurrency(salesPkrCommission)} Commission (PKR)</span>}
              {salesUsdCommission > 0 && <span className="badge free"><i className="fas fa-percent"></i> $ {fmtCurrency(salesUsdCommission)} Commission (USD)</span>}
              {salesPkrCollected === 0 && salesUsdCollected === 0 && <span className="badge neutral">No collections this period</span>}
            </div>
            <div className="group-footer-note">
              Top performer: {topSalesAgent?.sales_agent_name || 'N/A'}
            </div>
          </div>

          {/* 7. Revenue — full width */}
          {(() => {
            const { periodTotal, currentPeriod, prevPeriodTotal, growth } = getRevenuePeriodSummary();
            const periodLabel = revenueView === 'daily' ? 'Last 30 Days' : revenueView === 'monthly' ? 'Last 12 Months' : 'All Years';
            const currentLabel = revenueView === 'daily' ? 'Last 30 Days' : revenueView === 'monthly' ? 'This Month' : 'This Year';
            const prevLabel   = revenueView === 'monthly' ? 'Last Month' : revenueView === 'yearly' ? 'Last Year' : null;
            const curSymbol   = revenueCurrency === 'pkr' ? '₨ ' : revenueCurrency === 'usd' ? '$ ' : '';
            const fmtVal = v => v >= 1000000 ? `${curSymbol}${(v/1000000).toFixed(2)}M` : v >= 1000 ? `${curSymbol}${(v/1000).toFixed(1)}K` : `${curSymbol}${fmtCurrency(v)}`;
            return (
            <div className="stats-group-card revenue-card" style={{ '--group-color': '#16a34a' }}>
              {/* Card header */}
              <div className="group-header">
                <div className="group-icon" style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="group-title-area">
                  <h3>Revenue Analytics</h3>
                  <div className="group-total">{periodLabel} · Completed payments</div>
                </div>
                <Link to="/analytics" className="view-link" onClick={e => e.stopPropagation()}>
                  Full Analytics <i className="fas fa-arrow-right"></i>
                </Link>
              </div>

              {/* Period + currency controls */}
              <div className="rev-controls-row">
                <div className="ctrl-group">
                  {[['daily','Day'],['monthly','Month'],['yearly','Year']].map(([v,l]) => (
                    <button key={v} className={`ctrl-btn ${revenueView === v ? 'active' : ''}`} onClick={() => setRevenueView(v)}>{l}</button>
                  ))}
                </div>
                <div className="ctrl-group">
                  {[['total','Total'],['pkr','PKR'],['usd','US$']].map(([v,l]) => (
                    <button key={v} className={`ctrl-btn ${revenueCurrency === v ? `active ${v}` : ''}`} onClick={() => setRevenueCurrency(v)}>{l}</button>
                  ))}
                </div>
              </div>

              {/* KPI summary strip */}
              <div className="rev-kpi-strip">
                <div className="rev-kpi-item rev-kpi-primary">
                  <div className="rev-kpi-label">All-Time (PKR)</div>
                  <div className="rev-kpi-value">₨ {fmtCurrency(rev.total_pkr)}</div>
                  <div className="rev-kpi-sub">Completed PKR payments</div>
                </div>
                <div className="rev-kpi-item rev-kpi-primary">
                  <div className="rev-kpi-label">All-Time (USD)</div>
                  <div className="rev-kpi-value">$ {fmtCurrency(rev.total_usd)}</div>
                  <div className="rev-kpi-sub">Completed USD payments</div>
                </div>
                {(salesPkrCommission > 0 || salesUsdCommission > 0) && (
                  <div className="rev-kpi-item" style={{ borderLeft: '3px solid #7c3aed' }}>
                    <div className="rev-kpi-label">Commission Paid</div>
                    {salesPkrCommission > 0 && <div className="rev-kpi-value" style={{ fontSize: '1rem' }}>₨ {fmtCurrency(salesPkrCommission)}</div>}
                    {salesUsdCommission > 0 && <div className="rev-kpi-value" style={{ fontSize: '1rem' }}>$ {fmtCurrency(salesUsdCommission)}</div>}
                  </div>
                )}
                <div className="rev-kpi-item">
                  <div className="rev-kpi-label">{currentLabel}</div>
                  <div className="rev-kpi-value">{fmtVal(currentPeriod)}</div>
                  {revenueView !== 'daily' && (
                    <div className="rev-kpi-sub">
                      {revenueView === 'monthly' ? `₨ ${fmtCurrency(rev.month_pkr)} · $ ${fmtCurrency(rev.month_usd)}` : ''}
                    </div>
                  )}
                </div>
                {prevLabel && prevPeriodTotal > 0 && (
                  <div className="rev-kpi-item">
                    <div className="rev-kpi-label">{prevLabel}</div>
                    <div className="rev-kpi-value">{fmtVal(prevPeriodTotal)}</div>
                    <div className="rev-kpi-sub">&nbsp;</div>
                  </div>
                )}
                {growth !== null && (
                  <div className={`rev-kpi-item rev-kpi-growth ${Number(growth) >= 0 ? 'positive' : 'negative'}`}>
                    <div className="rev-kpi-label">vs {prevLabel}</div>
                    <div className="rev-kpi-value">
                      <i className={`fas fa-arrow-${Number(growth) >= 0 ? 'up' : 'down'}`}></i>
                      {Math.abs(growth)}%
                    </div>
                    <div className="rev-kpi-sub">Period-over-period</div>
                  </div>
                )}
                {revenueView === 'daily' && (
                  <div className="rev-kpi-item">
                    <div className="rev-kpi-label">Avg / Day (30d)</div>
                    <div className="rev-kpi-value">
                      {fmtVal(revenueGraphData.length > 0 ? periodTotal / revenueGraphData.length : 0)}
                    </div>
                    <div className="rev-kpi-sub">{revenueGraphData.filter(d => d.value > 0).length} active days</div>
                  </div>
                )}
              </div>

              {/* Bar chart */}
              {revenueGraphData.length === 0 ? (
                <div className="graph-empty">
                  <i className="fas fa-chart-bar"></i>
                  <span>No revenue data for this period</span>
                </div>
              ) : (
                <div className="rev-chart-container">
                  {/* Y-axis grid labels */}
                  <div className="rev-chart-y">
                    {[100, 75, 50, 25, 0].map(pct => (
                      <div key={pct} className="rev-y-label">
                        {pct > 0 ? fmtVal(maxRevenueVal * pct / 100) : '0'}
                      </div>
                    ))}
                  </div>
                  <div className="rev-chart-area">
                    {/* Grid lines */}
                    <div className="rev-grid-lines">
                      {[0,25,50,75,100].map(p => <div key={p} className="rev-grid-line" style={{ bottom: `${p}%` }}></div>)}
                    </div>
                    {/* Bars */}
                    <div className="rev-bars">
                      {revenueGraphData.map((item, i) => {
                        const heightPct = maxRevenueVal > 0 ? Math.max((item.value / maxRevenueVal) * 100, item.value > 0 ? 2 : 0) : 0;
                        const isLast = i === revenueGraphData.length - 1;
                        return (
                          <div key={i} className={`rev-bar-col2 ${isLast ? 'current' : ''}`} title={`${item.label}: ${fmtVal(item.value)}`}>
                            <div className="rev-bar-track2">
                              <div
                                className={`rev-bar-fill2 ${revenueCurrency} ${isLast ? 'current' : ''}`}
                                style={{ height: `${heightPct}%` }}
                              >
                                {item.value > 0 && heightPct >= 15 && (
                                  <span className="rev-bar-tooltip">{fmtVal(item.value)}</span>
                                )}
                              </div>
                            </div>
                            <div className={`rev-bar-label2 ${revenueView === 'daily' && revenueGraphData.length > 15 && i % 3 !== 0 ? 'hidden' : ''}`}>
                              {item.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        {/* ─── Sales Agent Report Card ─── */}
        {salesRows.length > 0 && (
          <div className="stats-group-card sa-report-card" style={{ '--group-color': '#7c3aed', marginBottom: 'var(--spacing-xl)' }}>
            <div className="group-header">
              <div className="group-icon" style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                <i className="fas fa-user-tie"></i>
              </div>
              <div className="group-title-area">
                <h3>Sales Agent Performance</h3>
                <div className="group-total">This month · {salesRows.length} agent{salesRows.length !== 1 ? 's' : ''} with assigned organizations</div>
              </div>
              <Link to="/admin/settings" state={{ activeTab: 'sales-agents' }} className="view-link" style={{ color: '#7c3aed' }} onClick={e => e.stopPropagation()}>
                Manage Agents <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            {/* Summary KPI strip */}
            {salesSummary && (
              <div className="sa-kpi-strip">
                <div className="sa-kpi-item">
                  <div className="sa-kpi-label">Total Agents</div>
                  <div className="sa-kpi-value">{salesSummary.agents_count ?? 0}</div>
                </div>
                <div className="sa-kpi-item">
                  <div className="sa-kpi-label">Orgs Covered</div>
                  <div className="sa-kpi-value">{salesSummary.organizations_assigned ?? 0}</div>
                  <div className="sa-kpi-sub">{salesSummary.active_organizations ?? 0} active</div>
                </div>
                <div className="sa-kpi-item">
                  <div className="sa-kpi-label">Payments (Month)</div>
                  <div className="sa-kpi-value">{salesSummary.completed_payments ?? 0}</div>
                </div>
                <div className="sa-kpi-item sa-collected">
                  <div className="sa-kpi-label">Collected PKR</div>
                  <div className="sa-kpi-value">₨ {fmtCurrency(salesPkrCollected)}</div>
                </div>
                <div className="sa-kpi-item sa-collected">
                  <div className="sa-kpi-label">Collected USD</div>
                  <div className="sa-kpi-value">$ {fmtCurrency(salesUsdCollected)}</div>
                </div>
                <div className="sa-kpi-item sa-commission">
                  <div className="sa-kpi-label">Commission PKR</div>
                  <div className="sa-kpi-value">₨ {fmtCurrency(salesPkrCommission)}</div>
                </div>
                <div className="sa-kpi-item sa-commission">
                  <div className="sa-kpi-label">Commission USD</div>
                  <div className="sa-kpi-value">$ {fmtCurrency(salesUsdCommission)}</div>
                </div>
              </div>
            )}

            {/* Agent table */}
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th className="sa-th-center">Orgs</th>
                    <th className="sa-th-right">Payments</th>
                    <th className="sa-th-right">Collected</th>
                    <th className="sa-th-right">Commission</th>
                    <th className="sa-th-center">Commission Rate</th>
                    <th className="sa-th-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {salesRows.map((row) => (
                    <tr key={row.sales_agent_id}>
                      <td>
                        <strong style={{ display: 'block' }}>{row.sales_agent_name}</strong>
                        {row.city && <small style={{ color: 'var(--text-light)', fontSize: '0.78rem' }}>{[row.city, row.country].filter(Boolean).join(', ')}</small>}
                      </td>
                      <td className="sa-td-center">{row.organizations_assigned}</td>
                      <td className="sa-td-right">{row.completed_payments}</td>
                      <td className="sa-td-right">{fmtCurrency(row.total_collected)} <small style={{ color: 'var(--text-light)' }}>{row.currency ?? ''}</small></td>
                      <td className="sa-td-right sa-comm">{fmtCurrency(row.commission_earned)} <small style={{ color: 'var(--text-light)' }}>{row.currency ?? ''}</small></td>
                      <td className="sa-td-center">{row.commission_display}</td>
                      <td className="sa-td-center">
                        <span className={`badge ${row.sales_agent_status === 1 ? 'success' : 'inactive'}`}>
                          {row.sales_agent_status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* System Activity + Core Actions */}
        <div className="main-content-grid">
          <div className="activity-section">
            <div className="section-header">
              <h3>System Activity</h3>
              <button className="refresh-btn" onClick={loadSystemData}><i className="fas fa-sync-alt"></i></button>
            </div>
            <div className="activity-list">
              {systemActivity.length === 0 ? (
                <div style={{ color: 'var(--text-light)', padding: '1rem 0' }}>No recent activity</div>
              ) : systemActivity.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className={`activity-icon ${activity.color}`}><i className={activity.icon}></i></div>
                  <div className="activity-content">
                    <div className="activity-message">{activity.message}</div>
                    <div className="activity-time">{activity.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="core-actions-section">
            <div className="maintenance-card">
              <div className="maintenance-header">
                <h4><i className="fas fa-tools"></i> Maintenance</h4>
              </div>
              <p className="maintenance-desc">Run system email and subscription maintenance tasks manually.</p>
              <div className="maintenance-actions">
                <button
                  className="maintenance-btn"
                  disabled={!!maintenanceRunning}
                  onClick={() => runMaintenanceAction('expiry')}
                >
                  {maintenanceRunning === 'expiry' ? 'Running...' : 'Run Expiry Notifications Only'}
                </button>
                <button
                  className="maintenance-btn"
                  disabled={!!maintenanceRunning}
                  onClick={() => runMaintenanceAction('queue')}
                >
                  {maintenanceRunning === 'queue' ? 'Running...' : 'Run Pending Queue Only'}
                </button>
                <button
                  className="maintenance-btn primary"
                  disabled={!!maintenanceRunning}
                  onClick={() => runMaintenanceAction('full')}
                >
                  {maintenanceRunning === 'full' ? 'Running...' : 'Run Full Maintenance'}
                </button>
                <button
                  className="maintenance-btn"
                  disabled={!!maintenanceRunning}
                  onClick={runIntegrationSyncAction}
                >
                  {maintenanceRunning === 'integration_sync' ? 'Running...' : 'Run Integration Sync Now'}
                </button>
              </div>
              {maintenanceError && <div className="maintenance-error">{maintenanceError}</div>}
              {maintenanceResult && (
                <div className="maintenance-result">
                  {maintenanceResult.expiry_notifications && (
                    <div>
                      Expiry: {maintenanceResult.expiry_notifications.reminders_sent || 0} reminders, {maintenanceResult.expiry_notifications.expired_sent || 0} expired notices sent
                    </div>
                  )}
                  {maintenanceResult.email_queue && (
                    <div>
                      Queue: {maintenanceResult.email_queue.sent || 0} sent, {maintenanceResult.email_queue.failed || 0} failed
                    </div>
                  )}
                  {maintenanceResult.attendance_integration_sync && (
                    <div>
                      <div>
                        Integration Sync: {maintenanceResult.attendance_integration_sync.organizations || 0} orgs, batch {maintenanceResult.attendance_integration_sync.batch_size || 0}
                      </div>
                      {Array.isArray(maintenanceResult.attendance_integration_sync.organization_ids) && (
                        <div>
                          Eligible Org IDs: {maintenanceResult.attendance_integration_sync.organization_ids.join(', ') || '-'}
                        </div>
                      )}
                      {Array.isArray(maintenanceResult.attendance_integration_sync.results) && maintenanceResult.attendance_integration_sync.results.length > 0 && (
                        <div style={{ marginTop: '0.35rem' }}>
                          {maintenanceResult.attendance_integration_sync.results.map((r, idx) => (
                            <div key={`sync-org-${idx}`}>
                              Org {r.org_id}: source {r.processed_source_rows || 0}, upserted {r.upserted_daily_rows || 0}
                              {r?.diagnostics?.zero_reason ? ` (${r.diagnostics.zero_reason})` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="maintenance-card">
              <div className="maintenance-header">
                <h4><i className="fas fa-database"></i> Backup</h4>
              </div>
              <p className="maintenance-desc">Provision and generate full web portal backup archives (folders, files, and system settings).</p>
              <div className="backup-form-grid">
                <label className="backup-check">
                  <input
                    type="checkbox"
                    checked={!!cloudBackupConfig.cloud_enabled}
                    onChange={(e) => setCloudBackupConfig((prev) => ({ ...prev, cloud_enabled: e.target.checked ? 1 : 0 }))}
                  />
                  Keep backup copy on Google Drive (via webhook)
                </label>
                <input
                  type="text"
                  className="backup-input"
                  value={cloudBackupConfig.google_drive_link}
                  onChange={(e) => setCloudBackupConfig((prev) => ({ ...prev, google_drive_link: e.target.value }))}
                  placeholder="Google Drive folder link"
                />
                <input
                  type="url"
                  className="backup-input"
                  value={cloudBackupConfig.cloud_webhook_url}
                  onChange={(e) => setCloudBackupConfig((prev) => ({ ...prev, cloud_webhook_url: e.target.value }))}
                  placeholder="Cloud upload webhook URL"
                />
                <button className="maintenance-btn" disabled={cloudBackupSaving} onClick={saveCloudBackupConfig}>
                  {cloudBackupSaving ? 'Saving...' : 'Save Cloud Backup Settings'}
                </button>
                <button className="maintenance-btn" disabled={cloudBackupTesting} onClick={testCloudBackupWebhook}>
                  {cloudBackupTesting ? 'Testing...' : 'Test Cloud Webhook'}
                </button>
                <input
                  type="text"
                  className="backup-input"
                  value={backupOptions.label}
                  onChange={(e) => setBackupOptions((prev) => ({ ...prev, label: e.target.value }))}
                  placeholder="Backup label (e.g. pre_release)"
                />
                <label className="backup-check">
                  <input
                    type="checkbox"
                    checked={backupOptions.include_uploads}
                    onChange={(e) => setBackupOptions((prev) => ({ ...prev, include_uploads: e.target.checked }))}
                  />
                  Include uploads
                </label>
                <label className="backup-check">
                  <input
                    type="checkbox"
                    checked={backupOptions.include_vendor}
                    onChange={(e) => setBackupOptions((prev) => ({ ...prev, include_vendor: e.target.checked }))}
                  />
                  Include vendor
                </label>
                <label className="backup-check">
                  <input
                    type="checkbox"
                    checked={backupOptions.include_node_modules}
                    onChange={(e) => setBackupOptions((prev) => ({ ...prev, include_node_modules: e.target.checked }))}
                  />
                  Include node_modules
                </label>
                <button
                  className="maintenance-btn primary"
                  disabled={backupRunning}
                  onClick={runBackupAction}
                >
                  {backupRunning ? 'Creating Backup...' : 'Create Backup Now'}
                </button>
              </div>
              {backupError && <div className="maintenance-error">{backupError}</div>}
              {cloudBackupTestResult && (
                <div className="maintenance-result">
                  Webhook Test: {cloudBackupTestResult.uploaded ? 'Success' : 'Failed'}
                  {cloudBackupTestResult.status ? <div>HTTP Status: {cloudBackupTestResult.status}</div> : null}
                  {cloudBackupTestResult.reason ? <div>Reason: {cloudBackupTestResult.reason}</div> : null}
                  {cloudBackupTestResult.error ? <div>Error: {cloudBackupTestResult.error}</div> : null}
                  {cloudBackupTestResult.response ? <div>Response: {String(cloudBackupTestResult.response).slice(0, 500)}</div> : null}
                </div>
              )}
              {backupRunning && backupRunProgress > 0 && (
                <div className="backup-progress-wrap">
                  <div className="backup-progress-label">Preparing backup... {backupRunProgress}%</div>
                  <div className="backup-progress-bar">
                    <span style={{ width: `${backupRunProgress}%` }} />
                  </div>
                </div>
              )}
              {backupResult && (
                <div className="maintenance-result">
                  Backup: {backupResult.file_name} ({Number(backupResult.file_size || 0).toLocaleString()} bytes), files: {backupResult.files_added || 0}
                  {backupResult.cloud_upload?.enabled && (
                    <div>
                      Cloud: {backupResult.cloud_upload.uploaded ? 'Uploaded' : `Not uploaded (${backupResult.cloud_upload.reason || 'unknown'})`}
                      {backupResult.cloud_upload.status ? ` - HTTP ${backupResult.cloud_upload.status}` : ''}
                      {backupResult.cloud_upload.response ? ` - ${String(backupResult.cloud_upload.response).slice(0, 180)}` : ''}
                    </div>
                  )}
                </div>
              )}
              {backupList.length > 0 && (
                <div className="backup-list">
                  {backupList.slice(0, 5).map((b) => (
                    <button key={b.file_name} className="backup-item backup-item-btn" onClick={() => handleDownloadBackup(b)}>
                      <span>{b.file_name}</span>
                      <small>{backupDownloadProgress[b.file_name] != null ? `Downloading ${backupDownloadProgress[b.file_name]}%` : b.created_at}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="maintenance-card">
              <div className="maintenance-header">
                <h4><i className="fas fa-server"></i> Database Backup</h4>
              </div>
              <p className="maintenance-desc">Configure automatic DB backup schedule and run manual SQL backup any time.</p>
              <div className="backup-form-grid">
                <label className="backup-check">
                  <input
                    type="checkbox"
                    checked={!!dbBackupConfig.auto_enabled}
                    onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, auto_enabled: e.target.checked ? 1 : 0 }))}
                  />
                  Enable automatic DB backup
                </label>
                <label className="backup-check">
                  Frequency (hours):
                  <input
                    type="number"
                    min="1"
                    max="168"
                    className="backup-input"
                    value={dbBackupConfig.frequency_hours}
                    onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, frequency_hours: e.target.value }))}
                  />
                </label>
                <label className="backup-check">
                  Keep last backups:
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="backup-input"
                    value={dbBackupConfig.keep_last}
                    onChange={(e) => setDbBackupConfig((prev) => ({ ...prev, keep_last: e.target.value }))}
                  />
                </label>
                <button className="maintenance-btn" disabled={dbBackupSaving} onClick={saveDatabaseBackupConfig}>
                  {dbBackupSaving ? 'Saving...' : 'Save DB Backup Settings'}
                </button>
                <button className="maintenance-btn primary" disabled={dbBackupRunning} onClick={runDatabaseBackupAction}>
                  {dbBackupRunning ? 'Creating DB Backup...' : 'Run DB Backup Now'}
                </button>
              </div>
              {dbBackupConfig.last_run_at && (
                <div className="maintenance-result">Last run: {dbBackupConfig.last_run_at}</div>
              )}
              {dbBackupError && <div className="maintenance-error">{dbBackupError}</div>}
              {dbBackupRunning && dbBackupRunProgress > 0 && (
                <div className="backup-progress-wrap">
                  <div className="backup-progress-label">Preparing DB backup... {dbBackupRunProgress}%</div>
                  <div className="backup-progress-bar">
                    <span style={{ width: `${dbBackupRunProgress}%` }} />
                  </div>
                </div>
              )}
              {dbBackupResult && (
                <div className="maintenance-result">
                  DB Backup: {dbBackupResult.file_name} ({Number(dbBackupResult.file_size || 0).toLocaleString()} bytes)
                  {dbBackupResult.cloud_upload?.enabled && (
                    <div>
                      Cloud: {dbBackupResult.cloud_upload.uploaded ? 'Uploaded' : `Not uploaded (${dbBackupResult.cloud_upload.reason || 'unknown'})`}
                      {dbBackupResult.cloud_upload.status ? ` - HTTP ${dbBackupResult.cloud_upload.status}` : ''}
                      {dbBackupResult.cloud_upload.response ? ` - ${String(dbBackupResult.cloud_upload.response).slice(0, 180)}` : ''}
                    </div>
                  )}
                </div>
              )}
              {dbBackupList.length > 0 && (
                <div className="backup-list">
                  {dbBackupList.slice(0, 5).map((b) => (
                    <button key={b.file_name} className="backup-item backup-item-btn" onClick={() => handleDownloadDbBackup(b)}>
                      <span>{b.file_name}</span>
                      <small>{dbBackupDownloadProgress[b.file_name] != null ? `Downloading ${dbBackupDownloadProgress[b.file_name]}%` : b.created_at}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="section-header"><h3>Quick Actions</h3></div>
            <div className="core-actions-grid">
              {coreAdminActions.map((action, index) => (
                <Link key={index} to={action.link} className={`core-action-card ${action.color}`}>
                  <div className="action-icon"><i className={action.icon}></i></div>
                  <div className="action-content">
                    <div className="action-title">{action.title}</div>
                    <div className="action-description">{action.description}</div>
                  </div>
                  <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .core-admin-dashboard { min-height: 100vh; background: var(--light-color); }

        /* Welcome */
        .welcome-section {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          color: var(--white);
          padding: var(--spacing-xl);
          border-radius: var(--radius-xl);
          margin-bottom: var(--spacing-xl);
          position: relative;
          overflow: hidden;
        }
        .welcome-section::before {
          content: '';
          position: absolute;
          top: -50%; right: -20%;
          width: 100%; height: 200%;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="cr" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M5 15 L10 5 L15 15 Z" fill="rgba(255,255,255,0.08)"/></pattern></defs><rect width="100" height="100" fill="url(%23cr)"/></svg>');
          transform: rotate(15deg);
        }
        .welcome-content { position: relative; z-index: 1; }
        .admin-badge {
          display: inline-flex; align-items: center; gap: var(--spacing-sm);
          background: rgba(255,255,255,0.2); padding: var(--spacing-sm) var(--spacing-md);
          border-radius: var(--radius-lg); font-size: 0.9rem; font-weight: 600; margin-bottom: var(--spacing-md);
        }
        .welcome-content h1 { font-size: 2.2rem; margin-bottom: var(--spacing-sm); font-weight: 700; }
        .welcome-content p { font-size: 1.1rem; opacity: 0.9; margin: 0; }

        /* Error */
        .error-message {
          background: rgba(231,76,60,0.1); color: var(--error);
          padding: var(--spacing-md); border-radius: var(--radius-md);
          margin-bottom: var(--spacing-xl); display: flex; align-items: center; gap: var(--spacing-sm);
        }

        /* Alerts */
        .alerts-section { margin-bottom: var(--spacing-xl); }
        .alerts-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-lg); }
        .alerts-section-header h3 { color: var(--text-dark); font-size: 1.2rem; margin: 0; display: flex; align-items: center; gap: 0.5rem; }
        .alerts-count { background: var(--error); color: #fff; font-size: 0.75rem; font-weight: 700; padding: 0.2rem 0.55rem; border-radius: 999px; }
        .alerts-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: var(--spacing-md); }
        .alert-card {
          padding: var(--spacing-md); border-radius: var(--radius-lg); border-left: 4px solid;
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .alert-card.clickable { cursor: pointer; }
        .alert-card.clickable:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
        .alert-card.critical { background: rgba(231,76,60,0.08); border-left-color: #e74c3c; }
        .alert-card.critical:hover { background: rgba(231,76,60,0.13); }
        .alert-card.warning { background: rgba(243,156,18,0.08); border-left-color: #f39c12; }
        .alert-card.warning:hover { background: rgba(243,156,18,0.13); }
        .alert-card.info { background: rgba(52,152,219,0.08); border-left-color: #3498db; }
        .alert-card.info:hover { background: rgba(52,152,219,0.13); }
        .alert-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 0.4rem; }
        .alert-title-row { display: flex; align-items: center; gap: 0.4rem; flex: 1; }
        .alert-icon-sm { font-size: 0.9rem; }
        .alert-card.critical .alert-icon-sm { color: #e74c3c; }
        .alert-card.warning .alert-icon-sm { color: #f39c12; }
        .alert-card.info .alert-icon-sm { color: #3498db; }
        .alert-title { font-weight: 600; color: var(--text-dark); font-size: 0.95rem; }
        .alert-dismiss { background: none; border: none; color: var(--text-light); cursor: pointer; padding: 0.1rem 0.3rem; border-radius: 4px; flex-shrink: 0; transition: color 0.15s; }
        .alert-dismiss:hover { color: var(--text-dark); }
        .alert-message { color: var(--text-dark); font-size: 0.88rem; margin-bottom: 0.4rem; line-height: 1.4; }
        .alert-action { font-size: 0.82rem; font-weight: 600; }
        .alert-card.critical .alert-action { color: #e74c3c; }
        .alert-card.warning .alert-action { color: #f39c12; }
        .alert-card.info .alert-action { color: #3498db; }

        /* ─── Stats Groups Grid ─── */
        .stats-groups-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-lg);
          margin-bottom: var(--spacing-xl);
        }

        .stats-group-card {
          background: var(--white);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-md);
          padding: var(--spacing-lg);
          border-top: 4px solid var(--group-color, #3498db);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .stats-group-card.clickable { cursor: pointer; }
        .stats-group-card.clickable:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg, 0 8px 25px rgba(0,0,0,0.12)); }

        /* Revenue card: full width */
        .revenue-card { grid-column: 1 / -1; }

        .group-header {
          display: flex; align-items: center; gap: var(--spacing-md);
          margin-bottom: var(--spacing-md);
        }
        .group-icon {
          width: 52px; height: 52px; border-radius: var(--radius-lg);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.4rem; color: #fff; flex-shrink: 0;
        }
        .group-title-area { flex: 1; }
        .group-title-area h3 { margin: 0 0 2px; font-size: 1.1rem; font-weight: 700; color: var(--text-dark); }
        .group-total { font-size: 0.85rem; color: var(--text-light); }
        .group-arrow { font-size: 1rem; color: var(--text-light); transition: transform 0.2s; }
        .stats-group-card.clickable:hover .group-arrow { transform: translateX(4px); color: var(--group-color, #3498db); }

        .view-link {
          font-size: 0.85rem; font-weight: 600; color: var(--group-color, #3498db);
          text-decoration: none; display: flex; align-items: center; gap: 0.3rem;
          white-space: nowrap;
        }
        .view-link:hover { text-decoration: underline; }

        /* Badges */
        .group-badges {
          display: flex; flex-wrap: wrap; gap: var(--spacing-sm);
          margin-bottom: var(--spacing-sm);
        }
        .group-badges.vertical { flex-direction: column; gap: 0.4rem; }

        .badge {
          display: inline-flex; align-items: center; gap: 0.35rem;
          padding: 0.3rem 0.7rem; border-radius: 999px;
          font-size: 0.82rem; font-weight: 600; white-space: nowrap;
        }
        .badge.success { background: rgba(39,174,96,0.12); color: #27ae60; }
        .badge.inactive { background: rgba(108,117,125,0.12); color: #6c757d; }
        .badge.free { background: rgba(52,152,219,0.12); color: #3498db; }
        .badge.overdue { background: rgba(231,76,60,0.12); color: #e74c3c; }
        .badge.warning { background: rgba(243,156,18,0.12); color: #f39c12; }
        .badge.danger { background: rgba(231,76,60,0.12); color: #e74c3c; }
        .badge.info { background: rgba(23,162,184,0.12); color: #17a2b8; }
        .badge.neutral { background: rgba(108,117,125,0.1); color: #6c757d; }

        .group-divider { height: 1px; background: var(--light-color); margin: var(--spacing-sm) 0; }
        .group-sub-label { font-size: 0.78rem; font-weight: 600; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
        .group-footer-note { font-size: 0.78rem; color: var(--text-light); margin-top: var(--spacing-sm); }
        .att-analytics-strip { display: flex; gap: 0.5rem; margin: 0.6rem 0 0.2rem; border-top: 1px solid rgba(0,0,0,0.06); padding-top: 0.6rem; }
        .att-kpi { flex: 1; text-align: center; }
        .att-kpi-label { font-size: 0.68rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.03em; }
        .att-kpi-value { font-size: 1.05rem; font-weight: 700; color: #9b59b6; margin-top: 0.15rem; }

        /* Staff tabs */
        .group-tabs {
          display: flex; gap: 0.3rem; margin: var(--spacing-sm) 0;
        }
        .tab-btn {
          padding: 0.3rem 0.75rem; border: 1px solid var(--light-color);
          background: var(--white); border-radius: var(--radius-sm);
          cursor: pointer; font-size: 0.82rem; color: var(--text-light);
          transition: all 0.15s;
        }
        .tab-btn.active { background: var(--group-color, #27ae60); color: #fff; border-color: var(--group-color, #27ae60); }
        .tab-btn:hover:not(.active) { background: var(--light-color); }

        /* Mini bars (staff summary) */
        .staff-summary-bars { margin-top: 0.5rem; }
        .mini-bar-row { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; }
        .mini-bar-label { width: 55px; font-size: 0.82rem; color: var(--text-light); }
        .mini-bar-track { flex: 1; height: 8px; background: var(--light-color); border-radius: 4px; overflow: hidden; }
        .mini-bar-fill { height: 100%; border-radius: 4px; transition: width 0.4s; }
        .mini-bar-fill.success { background: #27ae60; }
        .mini-bar-fill.danger { background: #e74c3c; }
        .mini-bar-pct { width: 36px; text-align: right; font-size: 0.82rem; color: var(--text-dark); font-weight: 600; }

        /* Breakdown list */
        .breakdown-list { max-height: 160px; overflow-y: auto; margin-top: 0.5rem; }
        .breakdown-row { display: flex; align-items: center; justify-content: space-between; padding: 0.3rem 0; border-bottom: 1px solid var(--light-color); gap: 0.5rem; }
        .breakdown-row:last-child { border-bottom: none; }
        .breakdown-name { flex: 1; font-size: 0.82rem; color: var(--text-dark); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .breakdown-org-name { display: block; font-size: 0.75rem; color: var(--text-light); }
        .breakdown-vals { display: flex; gap: 0.4rem; flex-shrink: 0; }
        .breakdown-legend { display: flex; gap: 0.4rem; justify-content: flex-end; margin-top: 0.3rem; }
        .breakdown-empty { font-size: 0.85rem; color: var(--text-light); padding: 0.5rem 0; }
        .bv { font-size: 0.78rem; font-weight: 600; padding: 0.15rem 0.5rem; border-radius: 999px; }
        .bv.total { background: rgba(52,152,219,0.12); color: #3498db; }
        .bv.active { background: rgba(39,174,96,0.12); color: #27ae60; }
        .bv.inactive { background: rgba(108,117,125,0.12); color: #6c757d; }

        /* Attendance ring */
        .attendance-ring-row { display: flex; align-items: center; gap: var(--spacing-lg); margin: var(--spacing-sm) 0; }
        .attendance-ring { position: relative; width: 80px; height: 80px; flex-shrink: 0; }
        .ring-svg { width: 100%; height: 100%; transform: rotate(-90deg); }
        .ring-bg { stroke: var(--light-color); }
        .ring-fill { stroke: #9b59b6; stroke-linecap: round; transition: stroke-dasharray 0.5s; }
        .ring-label {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          font-size: 1.1rem; font-weight: 700; color: var(--text-dark);
        }

        /* Revenue */
        .revenue-summary-grid {
          display: grid; grid-template-columns: repeat(6, 1fr); gap: var(--spacing-md);
          margin-bottom: var(--spacing-lg);
        }
        .rev-summary-item { padding: var(--spacing-md); border-radius: var(--radius-lg); text-align: center; }
        .rev-summary-item.collected { background: rgba(39,174,96,0.15); border: 1px solid rgba(39,174,96,0.3); }
        .rev-summary-item.collected-month { background: rgba(39,174,96,0.08); border: 1px solid rgba(39,174,96,0.15); }
        .rev-summary-item.pkr { background: rgba(39,174,96,0.05); }
        .rev-summary-item.usd { background: rgba(52,152,219,0.05); }
        .rev-summary-item.pkr-month { background: rgba(39,174,96,0.04); }
        .rev-summary-item.usd-month { background: rgba(52,152,219,0.04); }
        .rev-summary-label { font-size: 0.78rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.3rem; font-weight: 600; }
        .rev-summary-value { font-size: 1.3rem; font-weight: 700; color: var(--text-dark); }

        .graph-controls { display: flex; gap: var(--spacing-md); margin-bottom: var(--spacing-md); }
        .ctrl-group { display: flex; gap: 0.3rem; }
        .ctrl-btn {
          padding: 0.3rem 0.9rem; border: 1px solid var(--light-color);
          background: var(--white); border-radius: var(--radius-sm);
          cursor: pointer; font-size: 0.85rem; color: var(--text-light); transition: all 0.15s;
        }
        .ctrl-btn.active { background: var(--text-dark); color: #fff; border-color: var(--text-dark); }
        .ctrl-btn.active.total { background: #6c757d; border-color: #6c757d; }
        .ctrl-btn.active.pkr { background: #27ae60; border-color: #27ae60; }
        .ctrl-btn.active.usd { background: #3498db; border-color: #3498db; }
        .ctrl-btn:hover:not(.active) { background: var(--light-color); }

        .rev-chart {
          display: flex; align-items: flex-end; gap: 6px;
          height: 160px; padding: 0.5rem 0;
          overflow-x: auto;
        }
        .rev-bar-col { display: flex; flex-direction: column; align-items: center; flex: 1; min-width: 32px; }
        .rev-bar-value { font-size: 0.7rem; color: var(--text-light); margin-bottom: 3px; height: 1rem; }
        .rev-bar-track { flex: 1; width: 100%; display: flex; align-items: flex-end; }
        .rev-bar-fill {
          width: 100%; border-radius: 4px 4px 0 0; min-height: 2px;
          transition: height 0.4s; max-height: 100%;
        }
        .rev-bar-fill.total { background: linear-gradient(to top, #6c757d, #adb5bd); }
        .rev-bar-fill.pkr { background: linear-gradient(to top, #27ae60, #2ecc71); }
        .rev-bar-fill.usd { background: linear-gradient(to top, #3498db, #5dade2); }
        .rev-bar-label { font-size: 0.68rem; color: var(--text-light); margin-top: 4px; white-space: nowrap; }
        .graph-empty { color: var(--text-light); font-size: 0.9rem; padding: 2rem 0; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .graph-empty i { font-size: 2rem; opacity: 0.3; }

        /* Revenue controls row */
        .rev-controls-row {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 0.5rem;
          margin-bottom: var(--spacing-md);
        }

        /* KPI strip */
        .rev-kpi-strip {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: var(--spacing-sm);
          padding: var(--spacing-md) 0;
          border-top: 1px solid var(--light-color);
          border-bottom: 1px solid var(--light-color);
          margin-bottom: var(--spacing-lg);
        }
        .rev-kpi-item {
          padding: 0.6rem 0.8rem; border-radius: var(--radius-md);
          text-align: center;
        }
        .rev-kpi-item.rev-kpi-primary {
          background: rgba(22,163,74,0.08); border: 1px solid rgba(22,163,74,0.2);
        }
        .rev-kpi-item.rev-kpi-growth.positive {
          background: rgba(22,163,74,0.08); border: 1px solid rgba(22,163,74,0.2);
        }
        .rev-kpi-item.rev-kpi-growth.negative {
          background: rgba(231,76,60,0.08); border: 1px solid rgba(231,76,60,0.2);
        }
        .rev-kpi-label {
          font-size: 0.72rem; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 0.05em;
          font-weight: 600; margin-bottom: 0.25rem;
        }
        .rev-kpi-value {
          font-size: 1.3rem; font-weight: 700; color: var(--text-dark);
          line-height: 1.2; margin-bottom: 0.15rem;
        }
        .rev-kpi-item.rev-kpi-primary .rev-kpi-value { color: #16a34a; }
        .rev-kpi-item.rev-kpi-growth.positive .rev-kpi-value { color: #16a34a; }
        .rev-kpi-item.rev-kpi-growth.negative .rev-kpi-value { color: #e74c3c; }
        .rev-kpi-sub { font-size: 0.72rem; color: var(--text-light); }

        /* Professional bar chart */
        .rev-chart-container {
          display: flex; gap: 0.5rem;
          height: 210px; margin-top: var(--spacing-sm);
        }
        .rev-chart-y {
          display: flex; flex-direction: column; justify-content: space-between;
          width: 58px; flex-shrink: 0;
          padding: 0 0.4rem 22px 0; text-align: right;
        }
        .rev-y-label {
          font-size: 0.65rem; color: var(--text-light);
          line-height: 1; white-space: nowrap;
        }
        .rev-chart-area {
          flex: 1; position: relative; overflow: hidden;
          padding-bottom: 22px;
        }
        .rev-grid-lines {
          position: absolute; inset: 0 0 22px 0; pointer-events: none; z-index: 0;
        }
        .rev-grid-line {
          position: absolute; left: 0; right: 0;
          height: 1px; background: rgba(0,0,0,0.07);
        }
        .rev-bars {
          display: flex; align-items: flex-end; gap: 2px;
          height: 100%; overflow-x: auto;
          padding-bottom: 22px; position: relative; z-index: 1;
        }
        .rev-bar-col2 {
          display: flex; flex-direction: column; align-items: center;
          flex: 1; min-width: 16px; max-width: 56px;
          height: 100%; position: relative;
        }
        .rev-bar-track2 {
          flex: 1; width: 100%;
          display: flex; flex-direction: column; justify-content: flex-end;
          position: relative; border-radius: 4px 4px 0 0;
        }
        .rev-bar-col2.current .rev-bar-track2 {
          background: rgba(22,163,74,0.07);
        }
        .rev-bar-fill2 {
          width: 100%; border-radius: 4px 4px 0 0;
          transition: height 0.45s ease;
          position: relative; cursor: default;
        }
        .rev-bar-fill2:hover { filter: brightness(0.9); }
        .rev-bar-fill2.total         { background: linear-gradient(to top, #64748b, #94a3b8); }
        .rev-bar-fill2.pkr           { background: linear-gradient(to top, #15803d, #22c55e); }
        .rev-bar-fill2.usd           { background: linear-gradient(to top, #1d4ed8, #60a5fa); }
        .rev-bar-fill2.current.total { background: linear-gradient(to top, #475569, #64748b); }
        .rev-bar-fill2.current.pkr   { background: linear-gradient(to top, #14532d, #16a34a); }
        .rev-bar-fill2.current.usd   { background: linear-gradient(to top, #1e3a8a, #2563eb); }

        /* Tooltip inside bar */
        .rev-bar-tooltip {
          position: absolute; top: -1.5rem; left: 50%; transform: translateX(-50%);
          background: rgba(15,23,42,0.85); color: #fff;
          font-size: 0.62rem; font-weight: 600;
          padding: 2px 6px; border-radius: 4px;
          white-space: nowrap; pointer-events: none; z-index: 20;
          opacity: 0; transition: opacity 0.15s;
        }
        .rev-bar-fill2:hover .rev-bar-tooltip { opacity: 1; }

        /* Bar label */
        .rev-bar-label2 {
          font-size: 0.6rem; color: var(--text-light);
          white-space: nowrap; text-align: center;
          position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
          height: 20px; line-height: 20px;
        }
        .rev-bar-label2.hidden { visibility: hidden; }

        /* ─── Sales Agent Report Card ─── */
        .sa-report-card { grid-column: 1 / -1; }

        .sa-kpi-strip {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-md);
          padding: var(--spacing-md) 0;
          border-top: 1px solid var(--light-color);
          border-bottom: 1px solid var(--light-color);
        }
        .sa-kpi-item {
          padding: 0.5rem 0.75rem; border-radius: var(--radius-md);
          background: var(--light-color); text-align: center;
        }
        .sa-kpi-item.sa-collected { background: rgba(39,174,96,0.08); border: 1px solid rgba(39,174,96,0.2); }
        .sa-kpi-item.sa-commission { background: rgba(124,58,237,0.08); border: 1px solid rgba(124,58,237,0.2); }
        .sa-kpi-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-light); font-weight: 600; margin-bottom: 0.2rem; }
        .sa-kpi-value { font-size: 1.15rem; font-weight: 700; color: var(--text-dark); }
        .sa-kpi-item.sa-collected .sa-kpi-value { color: #15803d; }
        .sa-kpi-item.sa-commission .sa-kpi-value { color: #7c3aed; }
        .sa-kpi-sub { font-size: 0.7rem; color: var(--text-light); }

        .sa-table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sa-table {
          width: 100%; border-collapse: separate; border-spacing: 0;
          font-size: 0.88rem; border-bottom: 1px solid var(--light-color);
        }
        .sa-table thead th {
          background: #f1f5f9; color: var(--text-dark);
          padding: 8px 10px; text-align: left; font-size: 0.82rem;
          font-weight: 700; border-bottom: 2px solid #cbd5e1; white-space: nowrap;
        }
        .sa-th-right  { text-align: right !important; }
        .sa-th-center { text-align: center !important; }
        .sa-table tbody tr:nth-child(even) td { background: #f8fafc; }
        .sa-table tbody tr:hover td { background: rgba(124,58,237,0.04); }
        .sa-table tbody td { padding: 9px 10px; vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
        .sa-td-right  { text-align: right !important; white-space: nowrap; }
        .sa-td-center { text-align: center !important; }
        .sa-comm { color: #7c3aed !important; font-weight: 600; }

        /* Activity + Quick Actions */
        .main-content-grid {
          display: grid; grid-template-columns: 1fr 2fr; gap: var(--spacing-xl);
          margin-bottom: var(--spacing-xl);
        }
        .activity-section, .core-actions-section {
          background: var(--white); border-radius: var(--radius-xl);
          box-shadow: var(--shadow-md); padding: var(--spacing-xl);
        }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-lg); }
        .section-header h3 { font-size: 1.2rem; font-weight: 600; color: var(--text-dark); margin: 0; }
        .refresh-btn {
          background: none; border: none; color: var(--text-light);
          cursor: pointer; padding: 0.5rem; border-radius: var(--radius-sm); transition: all 0.15s;
        }
        .refresh-btn:hover { background: var(--light-color); color: var(--primary-color); }
        .activity-list { max-height: 320px; overflow-y: auto; }
        .activity-item { display: flex; align-items: center; gap: var(--spacing-md); padding: var(--spacing-md) 0; border-bottom: 1px solid var(--light-color); }
        .activity-item:last-child { border-bottom: none; }
        .activity-icon { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; color: #fff; flex-shrink: 0; }
        .activity-icon.success { background: var(--success); }
        .activity-icon.warning { background: var(--warning); }
        .activity-icon.info { background: var(--info); }
        .activity-message { font-size: 0.9rem; color: var(--text-dark); line-height: 1.4; }
        .activity-time { font-size: 0.78rem; color: var(--text-light); margin-top: 2px; }

        .maintenance-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 1rem;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
        }
        .maintenance-header h4 {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
          color: #1f2937;
          display: flex;
          align-items: center;
          gap: 0.45rem;
        }
        .maintenance-desc {
          margin: 0 0 0.75rem 0;
          font-size: 0.82rem;
          color: #6b7280;
          line-height: 1.45;
        }
        .maintenance-actions {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.55rem;
        }
        .maintenance-btn {
          border: 1px solid #d1d5db;
          background: #fff;
          color: #111827;
          border-radius: 8px;
          padding: 0.55rem 0.75rem;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          text-align: left;
          transition: all 0.16s ease;
        }
        .maintenance-btn:hover:not(:disabled) {
          border-color: #9ca3af;
          background: #f9fafb;
        }
        .maintenance-btn.primary {
          border-color: #0f766e;
          background: #0f766e;
          color: #fff;
        }
        .maintenance-btn.primary:hover:not(:disabled) {
          background: #115e59;
          border-color: #115e59;
        }
        .maintenance-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .maintenance-result {
          margin-top: 0.7rem;
          padding: 0.65rem 0.75rem;
          border-radius: 8px;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #065f46;
          font-size: 0.8rem;
          line-height: 1.45;
        }
        .maintenance-error {
          margin-top: 0.7rem;
          padding: 0.65rem 0.75rem;
          border-radius: 8px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
          font-size: 0.8rem;
        }
        .backup-form-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.5rem;
        }
        .backup-input {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 0.55rem 0.65rem;
          font-size: 0.82rem;
        }
        .backup-check {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          color: #374151;
          font-size: 0.82rem;
        }
        .backup-list {
          margin-top: 0.7rem;
          display: grid;
          gap: 0.35rem;
        }
        .backup-item {
          display: flex;
          justify-content: space-between;
          gap: 0.5rem;
          padding: 0.45rem 0.6rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          color: #111827;
          text-decoration: none;
          font-size: 0.78rem;
          background: #fff;
        }
        .backup-item-btn {
          width: 100%;
          border: 1px solid #e5e7eb;
          cursor: pointer;
          text-align: left;
        }
        .backup-item:hover {
          background: #f9fafb;
        }
        .backup-progress-wrap {
          margin-top: 0.65rem;
        }
        .backup-progress-label {
          font-size: 0.76rem;
          color: #334155;
          margin-bottom: 0.25rem;
        }
        .backup-progress-bar {
          width: 100%;
          height: 8px;
          background: #e5e7eb;
          border-radius: 999px;
          overflow: hidden;
        }
        .backup-progress-bar span {
          display: block;
          height: 100%;
          background: linear-gradient(90deg, #0f766e, #14b8a6);
          transition: width 0.35s ease;
        }
        .backup-download-inline {
          border: 1px solid #0f766e;
          background: #ffffff;
          color: #0f766e;
          border-radius: 8px;
          padding: 0.42rem 0.68rem;
          font-size: 0.78rem;
          font-weight: 700;
          cursor: pointer;
        }
        .backup-download-inline:hover {
          background: #f0fdfa;
        }

        /* Quick Actions */
        .core-actions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--spacing-md); }
        .core-action-card {
          display: flex; align-items: center; gap: var(--spacing-md);
          padding: var(--spacing-md); border-radius: var(--radius-lg);
          text-decoration: none; color: var(--text-dark);
          transition: all 0.2s; border: 2px solid transparent;
        }
        .core-action-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); text-decoration: none; color: var(--text-dark); }
        .core-action-card.primary { background: rgba(231,76,60,0.08); }
        .core-action-card.primary:hover { border-color: var(--primary-color); }
        .core-action-card.success { background: rgba(39,174,96,0.08); }
        .core-action-card.success:hover { border-color: var(--success); }
        .core-action-card.info { background: rgba(23,162,184,0.08); }
        .core-action-card.info:hover { border-color: var(--info); }
        .core-action-card.warning { background: rgba(243,156,18,0.08); }
        .core-action-card.warning:hover { border-color: var(--warning); }
        .core-action-card.purple { background: rgba(155,89,182,0.08); }
        .core-action-card.purple:hover { border-color: #9b59b6; }
        .core-action-card.teal { background: rgba(32,178,170,0.08); }
        .core-action-card.teal:hover { border-color: teal; }
        .core-action-card.secondary { background: rgba(108,117,125,0.08); }
        .core-action-card.secondary:hover { border-color: #6c757d; }
        .action-icon { font-size: 1.8rem; opacity: 0.8; flex-shrink: 0; }
        .action-content { flex: 1; }
        .action-title { font-size: 1rem; font-weight: 600; margin-bottom: 0.2rem; }
        .action-description { font-size: 0.82rem; color: var(--text-light); line-height: 1.4; }
        .action-arrow { font-size: 1rem; opacity: 0.4; transition: all 0.15s; }
        .core-action-card:hover .action-arrow { opacity: 1; transform: translateX(4px); }

        /* Responsive */
        @media (max-width: 1200px) {
          .revenue-summary-grid { grid-template-columns: repeat(3, 1fr); }
        }

        @media (max-width: 1024px) {
          .revenue-summary-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .welcome-content h1 { font-size: 1.7rem; }
          .stats-groups-grid { grid-template-columns: 1fr; }
          .revenue-card { grid-column: 1; }
          .main-content-grid { grid-template-columns: 1fr; }
          .revenue-summary-grid { grid-template-columns: repeat(2, 1fr); }
          .attendance-ring-row { flex-wrap: wrap; }
          .rev-kpi-strip { grid-template-columns: repeat(2, 1fr); }
          .rev-chart-container { height: 180px; }
          .rev-chart-y { width: 46px; font-size: 0.6rem; }
          .rev-kpi-value { font-size: 1.1rem; }
        }

        @media (max-width: 480px) {
          .rev-kpi-strip { grid-template-columns: 1fr 1fr; gap: 0.4rem; }
          .rev-controls-row { flex-direction: column; align-items: flex-start; }
          .rev-chart-container { height: 160px; }
          .rev-chart-y { width: 40px; }
        }
      `}</style>
    </div>
  );
};

export default CoreAdminDashboard;
